// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Appointment, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';

export interface UsePatientAppointmentsReturn {
  /** Upcoming appointments (booked, pending, arrived, or checked-in) */
  upcoming: Appointment[];
  /** Past appointments excluding cancellations and no-shows */
  history: Appointment[];
  /** Whether appointments are being loaded */
  loading: boolean;
  /** Any error that occurred during fetching */
  error: Error | null;
  /** Re-fetch appointments */
  refresh: () => void;
}

/**
 * Hook that fetches a given patient's appointments and splits them into
 * upcoming (active appointments that have not ended yet) and history (past
 * appointments excluding cancellations and no-shows).
 *
 * `pending` appointments are awaiting payment confirmation but are still
 * genuinely scheduled, so they belong in the upcoming list alongside `booked`.
 *
 * Provider-app variant: the patient is passed explicitly (the logged-in profile
 * is a Practitioner, not the patient being viewed).
 *
 * @param patient - Patient whose appointments to load
 * @returns Upcoming appointments, history, loading state, error, and refresh function
 */
export function usePatientAppointments(patient: Patient | undefined): UsePatientAppointmentsReturn {
  const medplum = useMedplum();
  const [upcoming, setUpcoming] = useState<Appointment[]>([]);
  const [history, setHistory] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const patientId = patient?.id;

  const fetchAppointments = useCallback(async () => {
    if (!patientId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const appointments = await medplum.searchResources('Appointment', {
        actor: `Patient/${patientId}`,
        _count: '100',
        _sort: '-date',
      });

      const now = new Date();
      const upcomingList: Appointment[] = [];
      const historyList: Appointment[] = [];

      const activeStatuses = ['booked', 'pending'];
      const excludedHistoryStatuses = ['cancelled', 'noshow'];

      for (const appt of appointments) {
        const apptEndIso = appt.end ?? appt.start;
        const apptEnd = apptEndIso ? new Date(apptEndIso) : null;
        const isActive = activeStatuses.includes(appt.status as string);
        const hasNotEnded = apptEnd ? apptEnd > now : false;
        const isUpcoming = isActive && hasNotEnded;

        if (isUpcoming) {
          upcomingList.push(appt);
        } else if (!excludedHistoryStatuses.includes(appt.status as string)) {
          historyList.push(appt);
        }
      }

      // Sort upcoming by nearest first
      upcomingList.sort((a, b) => {
        const aTime = a.start ? new Date(a.start).getTime() : 0;
        const bTime = b.start ? new Date(b.start).getTime() : 0;
        return aTime - bTime;
      });

      // Sort history by most recent first
      historyList.sort((a, b) => {
        const aTime = a.start ? new Date(a.start).getTime() : 0;
        const bTime = b.start ? new Date(b.start).getTime() : 0;
        return bTime - aTime;
      });

      setUpcoming(upcomingList);
      setHistory(historyList);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load appointments'));
    } finally {
      setLoading(false);
    }
    // Key on patient id — a new patient object reference with the same id must not trigger a re-fetch.
  }, [medplum, patientId]);

  useEffect(() => {
    fetchAppointments().catch(console.error);
  }, [fetchAppointments]);

  return { upcoming, history, loading, error, refresh: fetchAppointments };
}
